import { CoordinatorService } from './coordinator.service';
import { NotepadItem } from './notepad-item';
import { Quote } from './quote';
import { XItem } from './x-item';

export type EVIDENCE_TYPE = 'supporting' | 'opposing' | 'background';
type HAS_ID_TYPE = { id: string };

export class Evidence {
    private _referenceMap: Map<EVIDENCE_TYPE, Set<string>>;
    private _quoteMap: Map<EVIDENCE_TYPE, Map<string, Map<string, Quote>>>;

    constructor(
        public coords: CoordinatorService,
        public parent: NotepadItem,
        params: any,
    ) {
        this._referenceMap = new Map();
        this._referenceMap.set('supporting', new Set(params.supportingLs || []));
        this._referenceMap.set('opposing', new Set(params.opposingLs || []));
        this._referenceMap.set('background', new Set(params.backgroundLs || []));

        this._quoteMap = new Map();

        Object.entries(params.quotes || {}).forEach(([x, y]: [string, any]) => {
            const tpMap = new Map();
            this._quoteMap.set(x as EVIDENCE_TYPE, tpMap);

            Object.entries(y).forEach(([itmId, qObj]) => {
                let xitmIdMap = tpMap.get(itmId);
                if (!xitmIdMap) {
                    xitmIdMap = new Map();
                    tpMap.set(itmId, xitmIdMap);
                }

                Object.values(qObj as any).forEach((qParams) => {
                    const q = new Quote(this, qParams as any);
                    if (q.text) xitmIdMap.set(q.id, q);
                });
            });
        });
    }

    isOn(arg: string | HAS_ID_TYPE) {
        return (
            this.has('supporting', arg) || this.has('opposing', arg) || this.has('background', arg)
        );
    }

    add(type: EVIDENCE_TYPE, arg: string | HAS_ID_TYPE | (string | HAS_ID_TYPE)[]) {
        if (!Array.isArray(arg)) arg = [arg];
        arg.forEach((x) => {
            if (typeof x !== 'string') x = x.id;
            this._referenceMap.get(type)?.add(x);
        });
        this.parent.markForManifestStore();
        this.coords.display$.markForCheck();
    }

    getQuote(id: string) {
        for (const tpMp of this._quoteMap.values()) {
            for (const xitmMap of tpMp.values()) {
                const quote = xitmMap.get(id);
                if (quote) return quote;
            }
        }
        return '';
    }

    private _tpMap(type: EVIDENCE_TYPE) {
        let tpMap = this._quoteMap.get(type);
        if (!tpMap) {
            tpMap = new Map();
            this._quoteMap.set(type, tpMap);
        }
        return tpMap;
    }

    private _xitmMap(tpMap: Map<string, Map<string, Quote>>, xitmId: string) {
        let xitmMap = tpMap.get(xitmId);
        if (!xitmMap) {
            xitmMap = new Map();
            tpMap.set(xitmId, xitmMap);
        }
        return xitmMap;
    }

    addQuote(type: EVIDENCE_TYPE, xitm: XItem, text: string) {
        const hasText = !!this.coords.utils$.htmlTextContent(text);
        if (!hasText) return;

        const rSet = this._referenceMap.get(type)!;
        if (!rSet.has(xitm.id)) rSet.add(xitm.id);

        const tpMap = this._tpMap(type);

        const q = new Quote(this, { text });
        const xitmMap = this._xitmMap(tpMap, xitm.id);
        xitmMap.set(q.id, q);
        this._finalizeQuote();
    }

    private _finalizeQuote() {
        this.parent.markForManifestStore();
        this.coords.display$.markForCheck();
        this.coords.display$.closeDialog();
    }

    get allQuotes() {
        const quoteLs: Quote[] = [];
        for (const tpMp of this._quoteMap.values()) {
            for (const xitmMap of tpMp.values()) {
                quoteLs.push(...Array.from(xitmMap.values()));
            }
        }
        return quoteLs;
    }

    typeQuoteLs(type: EVIDENCE_TYPE) {
        const tpMap = this._tpMap(type);
        if (tpMap.size) {
            console.log(1111, this._quoteMap);
            console.log('tpMap', tpMap);
        }
        const quoteLs: Quote[] = [];
        for (const xitmMap of tpMap.values()) {
            quoteLs.push(...Array.from(xitmMap.values()));
        }
        return quoteLs;
    }

    hasText(txt: string) {
        for (const q of this.allQuotes) {
            if (this.coords.utils$.htmlTextContent(q.text).includes(txt)) return true;
        }
        return false;
    }

    changeQuoteType(q: Quote, newType: EVIDENCE_TYPE) {
        for (const tpMp of this._quoteMap.values()) {
            for (const [xitmId, xitmMap] of tpMp.entries()) {
                if (xitmMap.has(q.id)) {
                    xitmMap.delete(q.id);
                    const newTpMap = this._tpMap(newType);
                    const newXitmMap = this._xitmMap(newTpMap, xitmId);
                    this._referenceMap.get(newType)?.add(xitmId);
                    newXitmMap.set(q.id, q);
                    this._finalizeQuote();
                    return;
                }
            }
        }
    }

    private _deleteItemQuotes(type: EVIDENCE_TYPE, xitmId: string) {
        const tpMap = this._tpMap(type);
        tpMap.delete(xitmId);
    }

    deleteQuote(q: Quote) {
        for (const tpMp of this._quoteMap.values()) {
            for (const xitmMap of tpMp.values()) {
                const quote = xitmMap.get(q.id);
                if (xitmMap.has(q.id)) {
                    xitmMap.delete(q.id);
                    this._finalizeQuote();
                    return;
                }
            }
        }
    }

    quoteLs(type: EVIDENCE_TYPE, xitm: XItem): Quote[] {
        const tpMap = this._quoteMap.get(type);
        if (!tpMap) return [];
        const xitmMap = tpMap.get(xitm.id);
        if (!xitmMap) return [];
        return Array.from(xitmMap.values());
    }

    remove(type: EVIDENCE_TYPE, arg: string | HAS_ID_TYPE | (string | HAS_ID_TYPE)[], quote?: any) {
        if (quote) {
            console.log('Deleting quote:', quote);
            return;
        }

        if (!Array.isArray(arg)) arg = [arg];
        arg.forEach((x) => {
            if (typeof x !== 'string') x = x.id;
            this._referenceMap.get(type)?.delete(x);
            this._deleteItemQuotes(type, x);
        });
        this.parent.markForManifestStore();
        this.coords.display$.markForCheck();
    }

    has(type: EVIDENCE_TYPE, arg: string | HAS_ID_TYPE): boolean {
        if (typeof arg !== 'string') arg = arg.id;
        return !!this._referenceMap.get(type)?.has(arg);
    }

    save() {
        const quotes = this.coords.utils$.toJSONready(this._quoteMap);
        return {
            supportingLs: Array.from(this._referenceMap.get('supporting') || []),
            opposingLs: Array.from(this._referenceMap.get('opposing') || []),
            backgroundLs: Array.from(this._referenceMap.get('background') || []),
            quotes,
        };
    }

    refLs(type: EVIDENCE_TYPE): XItem[] {
        const ls: XItem[] = [];
        this._referenceMap.get(type)?.forEach((id) => {
            const xitm = this.coords.dataset$.main.get(id);
            xitm && ls.push(xitm);
        });
        return ls;
    }

    get xitemIdSet() {
        const s = new Set<string>();
        this._referenceMap.forEach((set) => {
            set.forEach((id) => s.add(id));
        });
        return s;
    }
}
